/**
 * Tabela de usuários com ordenação por header e paginação server-side.
 * Renderização condicional: skeleton durante loading, empty state quando vazio.
 */
import React from "react";
import * as MUI from "@mui/material";
import * as Icon from "@mui/icons-material";

import {
  FiltrosOperativa,
  OrderByColumn,
  UsuarioOperativaItem,
} from "../../../../interface/usuarioOperativa";
import { MetaPaginacao } from "../../../../hooks/useUsuariosOperativa";
import { AcoesUsuario } from "./AcoesUsuario";

// ---------- Sub-componentes auxiliares ----------------------------------

/** Converte a string de base para exibição e cor do chip */
const BASE_CONFIG: Record<
  string,
  { label: string; color: MUI.ChipProps["color"] }
> = {
  pm: { label: "PM", color: "primary" },
  pc: { label: "PC", color: "secondary" },
  bombeiro: { label: "BM", color: "error" },
  pericia: { label: "PE", color: "warning" },
};

const ChipBase: React.FC<{ base: string }> = ({ base }) => {
  const config = BASE_CONFIG[base?.toLowerCase()] ?? {
    label: base?.toUpperCase() || "—",
    color: "default" as const,
  };
  return <MUI.Chip label={config.label} color={config.color} size="small" />;
};

/** Chip colorido por nível de acesso */
const ChipAcesso: React.FC<{ idAcesso: number; descricao: string }> = ({
  idAcesso,
  descricao,
}) => {
  const colorMap: Record<number, MUI.ChipProps["color"]> = {
    0: "default",
    1: "warning",
    2: "error",
  };
  return (
    <MUI.Chip
      label={descricao}
      color={colorMap[idAcesso] ?? "default"}
      size="small"
      variant="outlined"
    />
  );
};

/** Skeleton de carregamento — exibe N linhas fantasmas */
const SkeletonLinhas: React.FC<{ quantidade: number; colunas: number }> = ({
  quantidade,
  colunas,
}) => (
  <>
    {Array.from({ length: quantidade }).map((_, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <MUI.TableRow key={i}>
        {Array.from({ length: colunas }).map((__, j) => (
          // eslint-disable-next-line react/no-array-index-key
          <MUI.TableCell key={j}>
            <MUI.Skeleton variant="text" />
          </MUI.TableCell>
        ))}
      </MUI.TableRow>
    ))}
  </>
);

/**
 * Texto truncado com tooltip — corta em `maxChars` e exibe o valor completo no hover.
 * Se o texto couber, renderiza sem tooltip para não poluir a UX.
 */
const TextoTruncado: React.FC<{ texto: string | null; maxChars?: number }> = ({
  texto,
  maxChars = 18,
}) => {
  if (!texto) return <span>—</span>;
  if (texto.length <= maxChars) return <span>{texto}</span>;
  return (
    <MUI.Tooltip title={texto} placement="top" arrow>
      <span style={{ cursor: "default", whiteSpace: "nowrap" }}>
        {texto.slice(0, maxChars)}…
      </span>
    </MUI.Tooltip>
  );
};

/**
 * Exibe quantidade de sistemas e, no hover, lista os chips completos.
 * Quando há apenas 1 sistema, mostra o nome direto (cabe no espaço).
 */
const SistemasResumido: React.FC<{
  sistemas: Array<{ ID_SISTEMA: number; NM_SISTEMA: string }>;
}> = ({ sistemas }) => {
  if (sistemas.length === 0) return <span>—</span>;
  if (sistemas.length === 1) {
    return (
      <MUI.Chip
        label={sistemas[0].NM_SISTEMA}
        size="small"
        variant="outlined"
      />
    );
  }
  const tooltipSistemas = (
    <MUI.Stack gap={0.5} p={0.5}>
      {sistemas.map((s) => (
        <MUI.Chip
          key={s.ID_SISTEMA}
          label={s.NM_SISTEMA}
          size="small"
          variant="outlined"
          sx={{ color: "inherit", borderColor: "rgba(255,255,255,0.5)" }}
        />
      ))}
    </MUI.Stack>
  );
  return (
    <MUI.Tooltip placement="top" arrow title={tooltipSistemas}>
      <MUI.Chip
        label={`${sistemas.length} sistemas`}
        size="small"
        variant="outlined"
        icon={<Icon.Layers fontSize="small" />}
        sx={{ cursor: "default" }}
      />
    </MUI.Tooltip>
  );
};

/** Estado vazio — exibido após primeira busca sem resultados */
const EstadoVazio: React.FC = () => (
  <MUI.TableRow>
    <MUI.TableCell colSpan={10} align="center" sx={{ py: 6 }}>
      <MUI.Stack alignItems="center" gap={1}>
        <Icon.SearchOff sx={{ fontSize: 48, color: "text.disabled" }} />
        <MUI.Typography color="text.secondary">
          Nenhum usuário encontrado para os filtros aplicados.
        </MUI.Typography>
      </MUI.Stack>
    </MUI.TableCell>
  </MUI.TableRow>
);

// ---------- Colunas ordenáveis -----------------------------------------

interface Coluna {
  id: string;
  label: string;
  orderByKey?: OrderByColumn;
  align?: MUI.TableCellProps["align"];
  minWidth?: number;
}

const COLUNAS: Coluna[] = [
  { id: "NOME", label: "Nome", orderByKey: "NOME", minWidth: 200 },
  { id: "CPF", label: "CPF", minWidth: 130 },
  { id: "MATRICULA", label: "Matrícula", minWidth: 110 },
  { id: "CARGO", label: "Cargo", orderByKey: "CARGO", minWidth: 150 },
  {
    id: "BASE",
    label: "Base",
    orderByKey: "BASE",
    minWidth: 80,
    align: "center",
  },
  { id: "NM_UNID_OPERACIONAL", label: "Unidade Operacional", minWidth: 180 },
  { id: "ID_ACESSO", label: "Acesso", minWidth: 100, align: "center" },
  { id: "SISTEMAS", label: "Sistemas", minWidth: 160 },
  {
    id: "CREATED_AT",
    label: "Criado em",
    orderByKey: "CREATED_AT",
    minWidth: 120,
  },
  { id: "ACOES", label: "Ações", minWidth: 120, align: "center" },
];

// ---------- Props principais -------------------------------------------

interface TabelaUsuariosProps {
  data: UsuarioOperativaItem[];
  meta: MetaPaginacao;
  filtros: FiltrosOperativa;
  loading: boolean;
  iniciado: boolean;
  callerAcesso: string;
  callerGrupo: number;
  onTrocarOrdenacao(coluna: OrderByColumn): void;
  onTrocarPagina(novaPagina: number): void;
  onTrocarLimitePorPagina(novoLimit: number): void;
}

// ---------- Componente principal ---------------------------------------

export const TabelaUsuarios: React.FC<TabelaUsuariosProps> = ({
  data,
  meta,
  filtros,
  loading,
  iniciado,
  callerAcesso,
  callerGrupo,
  onTrocarOrdenacao,
  onTrocarPagina,
  onTrocarLimitePorPagina,
}) => {
  const formatarData = (iso: string) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("pt-BR");
    } catch {
      return iso;
    }
  };

  return (
    <MUI.Paper variant="outlined" sx={{ overflow: "hidden" }}>
      {/* Barra de progresso linear durante carregamento */}
      {loading && <MUI.LinearProgress />}

      <MUI.TableContainer
        sx={{ maxHeight: "calc(100vh - 340px)", overflowX: "auto" }}
      >
        <MUI.Table stickyHeader size="small">
          <MUI.TableHead>
            <MUI.TableRow>
              {COLUNAS.map((col) => (
                <MUI.TableCell
                  key={col.id}
                  align={col.align}
                  sx={{
                    minWidth: col.minWidth,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col.orderByKey ? (
                    <MUI.TableSortLabel
                      active={filtros.orderBy === col.orderByKey}
                      direction={
                        filtros.orderBy === col.orderByKey
                          ? (filtros.orderDir.toLowerCase() as "asc" | "desc")
                          : "asc"
                      }
                      onClick={() => onTrocarOrdenacao(col.orderByKey!)}
                    >
                      {col.label}
                    </MUI.TableSortLabel>
                  ) : (
                    col.label
                  )}
                </MUI.TableCell>
              ))}
            </MUI.TableRow>
          </MUI.TableHead>

          <MUI.TableBody>
            {/* eslint-disable-next-line no-nested-ternary */}
            {loading && data.length === 0 ? (
              <SkeletonLinhas
                quantidade={filtros.limit}
                colunas={COLUNAS.length}
              />
            ) : iniciado && data.length === 0 ? (
              <EstadoVazio />
            ) : (
              data.map((usuario) => (
                <MUI.TableRow
                  key={usuario.ID_USUARIO}
                  hover
                  sx={{ "&:last-child td": { border: 0 } }}
                >
                  <MUI.TableCell sx={{ whiteSpace: "nowrap" }}>
                    <MUI.Stack>
                      <MUI.Typography variant="body2" fontWeight={500}>
                        {usuario.NOME || "—"}
                      </MUI.Typography>
                      {usuario.EMAIL && (
                        <MUI.Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {usuario.EMAIL}
                        </MUI.Typography>
                      )}
                    </MUI.Stack>
                  </MUI.TableCell>

                  <MUI.TableCell
                    sx={{ whiteSpace: "nowrap", fontFamily: "monospace" }}
                  >
                    {usuario.CPF || "—"}
                  </MUI.TableCell>

                  <MUI.TableCell>{usuario.MATRICULA || "—"}</MUI.TableCell>

                  <MUI.TableCell sx={{ maxWidth: 140 }}>
                    <TextoTruncado texto={usuario.CARGO} maxChars={18} />
                  </MUI.TableCell>

                  <MUI.TableCell align="center">
                    {usuario.BASE ? <ChipBase base={usuario.BASE} /> : "—"}
                  </MUI.TableCell>

                  <MUI.TableCell sx={{ maxWidth: 160 }}>
                    <TextoTruncado
                      texto={usuario.NM_UNID_OPERACIONAL}
                      maxChars={20}
                    />
                  </MUI.TableCell>

                  <MUI.TableCell align="center">
                    <ChipAcesso
                      idAcesso={usuario.ID_ACESSO.ID_ACESSO}
                      descricao={usuario.ID_ACESSO.DESCRICAO}
                    />
                  </MUI.TableCell>

                  <MUI.TableCell sx={{ maxWidth: 130 }}>
                    <SistemasResumido sistemas={usuario.SISTEMAS} />
                  </MUI.TableCell>

                  <MUI.TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatarData(usuario.CREATED_AT)}
                  </MUI.TableCell>

                  <MUI.TableCell align="center">
                    <AcoesUsuario
                      usuario={usuario}
                      callerAcesso={callerAcesso}
                      callerGrupo={callerGrupo}
                    />
                  </MUI.TableCell>
                </MUI.TableRow>
              ))
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>

      {/* Paginação */}
      <MUI.Divider />
      <MUI.TablePagination
        component="div"
        count={meta.total}
        page={meta.page - 1} // MUI usa índice base-0; API usa base-1
        rowsPerPage={filtros.limit}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(_, newPage) => onTrocarPagina(newPage + 1)}
        // eslint-disable-next-line react/jsx-curly-newline
        onRowsPerPageChange={(e) =>
          onTrocarLimitePorPagina(Number(e.target.value))
        }
        labelRowsPerPage="Por página:"
        // eslint-disable-next-line react/jsx-curly-newline
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </MUI.Paper>
  );
};
